import {
  dispatchMfaEnrolmentRequired,
  dispatchSessionExpired,
  SessionExpiredError
} from "@utils/authErrors";
import { catchSentryError } from "@utils/sentry";
import { del, get, post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  ApiBatchRequest,
  ApiListResponse,
  ApiRequest,
  ApiResponse
} from "catchment-common/interfaces/api";

type APIRequest = {
  body?: { [key: string]: any };
  queryStringParameters?: { [key: string]: any };
};

const API_NAME = "api";

const cleanQueryParams = (
  params: Record<string, unknown>
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    out[key] = value instanceof Date ? value.toISOString() : String(value);
  }
  return out;
};

const getAuthHeaders = async (
  forceRefresh = false
): Promise<Record<string, string> | undefined> => {
  const session = await fetchAuthSession({ forceRefresh });
  // The API Gateway custom authorizer verifies with tokenUse: "access", so the
  // access token is the only token it accepts — never fall back to the id token,
  // which the authorizer always rejects (guaranteed 401). A missing access token
  // therefore means we have no usable credentials at all.
  const token = session.tokens?.accessToken?.toString();
  return token ? { Authorization: token } : undefined;
};

// Backend gate: an authenticated request rejected with 403 because the user's
// org requires MFA and they have not enrolled. Distinct from 401 — do NOT sign
// them out; signal the auth layer to route them to forced enrolment instead.
// The 403 can surface either as a resolved response (status + body.code) or as
// a thrown Amplify error, so both call sites check.
const MFA_CODE = "MFA_ENROLMENT_REQUIRED";
// The 403 body arrives already-parsed from parseRestResponse (body.json()), but
// as a raw JSON *string* on a thrown Amplify ApiError (error.response.body is
// typed `string`). Accept both, else the mid-session gate silently no-ops on
// the thrown path and the user is never routed to enrolment.
const mfaCodeOf = (body: unknown): string | undefined => {
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch {
      return undefined;
    }
  }
  return typeof body === "object" && body !== null
    ? (body as { code?: string }).code
    : undefined;
};
const isMfaEnrolmentRequired = (
  status: number | undefined,
  body: unknown
): boolean => status === 403 && mfaCodeOf(body) === MFA_CODE;

const isUnauthorizedError = (error: any): boolean => {
  const statusCode =
    error?.response?.statusCode ||
    error?.$metadata?.httpStatusCode ||
    error?.statusCode;
  return statusCode === 401;
};

// Mark the session as terminally unauthorized: notify the auth layer (so it can
// sign the user out and redirect to login) and surface a typed error that the
// Sentry helper deliberately does not report as a crash.
const sessionExpired = (cause: unknown): SessionExpiredError => {
  dispatchSessionExpired();
  return new SessionExpiredError(cause);
};

const withAuthRetry = async <T,>(
  request: (headers?: Record<string, string>) => Promise<T>
): Promise<T> => {
  try {
    const headers = await getAuthHeaders();
    return await request(headers);
  } catch (error) {
    if (!isUnauthorizedError(error)) {
      throw error;
    }
    // First 401 — force a token refresh and retry once.
    const refreshedHeaders = await getAuthHeaders(true);
    if (!refreshedHeaders) {
      // The refresh produced no access token (refresh token expired/revoked) —
      // the session is dead; retrying would only 401 again.
      throw sessionExpired(error);
    }
    try {
      return await request(refreshedHeaders);
    } catch (retryError) {
      if (isUnauthorizedError(retryError)) {
        // Still unauthorized with a freshly minted token — terminal.
        throw sessionExpired(retryError);
      }
      throw retryError;
    }
  }
};

const parseRestResponse = async (operation: {
  response: Promise<{
    body: { json: () => Promise<unknown> };
    statusCode: number;
  }>;
}) => {
  const { body, statusCode } = await operation.response;
  let data: unknown = undefined;
  try {
    data = await body.json();
  } catch (_error) {
    data = undefined;
  }
  if (isMfaEnrolmentRequired(statusCode, data)) {
    dispatchMfaEnrolmentRequired();
  }
  return {
    data,
    status: statusCode
  };
};

const handleApiError = (error: unknown, context: string): never => {
  // MFA gate thrown as an error (rather than a resolved 403): route to enrolment,
  // do not report as a crash.
  const status =
    (error as any)?.response?.statusCode ?? (error as any)?.statusCode;
  const body = (error as any)?.response?.body ?? (error as any)?.body;
  if (isMfaEnrolmentRequired(status, body)) {
    dispatchMfaEnrolmentRequired();
    throw error;
  }
  catchSentryError(error, true, {
    extra: { timestamp: new Date().toISOString() },
    tags: { apiContext: context }
  });
  throw error;
};

export const getApiData = async (
  path: string,
  callFn: string,
  id: string,
  values: { [key: string]: number | string | Date | string[] | undefined } = {}
): Promise<ApiResponse> => {
  try {
    const myInit = {
      queryStringParameters: cleanQueryParams({
        callFn,
        id,
        path,
        ...values
      })
    } as APIRequest;
    const res = await withAuthRetry((headers) =>
      parseRestResponse(
        get({
          apiName: API_NAME,
          options: {
            headers,
            queryParams: myInit.queryStringParameters
          },
          path: "api"
        })
      )
    );
    return res as ApiResponse;
  } catch (error) {
    handleApiError(error, `getApiData:${path}:${callFn}`);
    throw error;
  }
};

export const searchApiData = async (
  path: string,
  callFn: string,
  requestData: string[],
  values: { [key: string]: number | string | Date | undefined } = {}
): Promise<ApiListResponse> => {
  try {
    const myInit = {
      body: {
        callFn,
        data: requestData,
        path,
        ...values
      }
    } as APIRequest;
    const res = await withAuthRetry((headers) =>
      parseRestResponse(
        post({
          apiName: API_NAME,
          options: {
            body: myInit.body,
            headers
          },
          path: "api"
        })
      )
    );
    return res as ApiListResponse;
  } catch (error) {
    handleApiError(error, `searchApiData:${path}:${callFn}`);
    throw error;
  }
};

export const listApiData = async (
  path: string,
  callFn: string,
  id?: string,
  values: { [key: string]: number | string | Date | undefined } = {}
): Promise<ApiListResponse> => {
  try {
    const myInit = {
      queryStringParameters: cleanQueryParams({
        callFn,
        id,
        path,
        ...values
      })
    } as APIRequest;
    const res = await withAuthRetry((headers) =>
      parseRestResponse(
        get({
          apiName: API_NAME,
          options: {
            headers,
            queryParams: myInit.queryStringParameters
          },
          path: "api"
        })
      )
    );
    return res as ApiListResponse;
  } catch (error) {
    handleApiError(error, `listApiData:${path}:${callFn}`);
    throw error;
  }
};

export const postApiData = async (
  path: string,
  callFn: string,
  requestData: ApiRequest
): Promise<ApiResponse> => {
  try {
    const myInit = {
      body: {
        callFn,
        path,
        ...requestData
      }
    } as APIRequest;
    const res = await withAuthRetry((headers) =>
      parseRestResponse(
        post({
          apiName: API_NAME,
          options: {
            body: myInit.body,
            headers
          },
          path: "api"
        })
      )
    );
    return res as ApiResponse;
  } catch (error) {
    handleApiError(error, `postApiData:${path}:${callFn}`);
    throw error;
  }
};

export const postBatchApiData = async (
  path: string,
  callFn: string,
  requestData: ApiBatchRequest
): Promise<ApiResponse> => {
  try {
    const myInit = {
      body: {
        callFn,
        data: requestData,
        path
      }
    } as APIRequest;
    const res = await withAuthRetry((headers) =>
      parseRestResponse(
        post({
          apiName: API_NAME,
          options: {
            body: myInit.body,
            headers
          },
          path: "api"
        })
      )
    );
    return res as ApiResponse;
  } catch (error) {
    handleApiError(error, `postBatchApiData:${path}:${callFn}`);
    throw error;
  }
};

export const deleteBatchApiData = async (
  path: string,
  callFn: string,
  requestData: ApiBatchRequest
): Promise<ApiResponse> => {
  try {
    const myInit = {
      body: {
        callFn,
        data: requestData,
        path
      }
    } as APIRequest;
    const res = await withAuthRetry((headers) =>
      parseRestResponse(
        del({
          apiName: API_NAME,
          options: {
            body: myInit.body,
            headers
          },
          path: "api"
        })
      )
    );
    return res as ApiResponse;
  } catch (error) {
    handleApiError(error, `deleteBatchApiData:${path}:${callFn}`);
    throw error;
  }
};

// Unauthenticated GET — for public endpoints (e.g. employee invite validate)
export const getPublicApiData = async (
  path: string,
  callFn: string,
  values: { [key: string]: string | undefined } = {}
): Promise<ApiResponse> => {
  try {
    const myInit = {
      queryStringParameters: cleanQueryParams({ callFn, path, ...values })
    } as APIRequest;
    const res = await parseRestResponse(
      get({
        apiName: API_NAME,
        options: { queryParams: myInit.queryStringParameters },
        path: "api"
      })
    );
    return res as ApiResponse;
  } catch (error) {
    handleApiError(error, `getPublicApiData:${path}:${callFn}`);
    throw error;
  }
};

// Unauthenticated POST — for public endpoints (e.g. employee invite submit)
export const postPublicApiData = async (
  path: string,
  callFn: string,
  requestData: Record<string, unknown>
): Promise<ApiResponse> => {
  try {
    const myInit = {
      body: { callFn, path, ...requestData }
    } as APIRequest;
    const res = await parseRestResponse(
      post({
        apiName: API_NAME,
        options: { body: myInit.body },
        path: "api"
      })
    );
    return res as ApiResponse;
  } catch (error) {
    handleApiError(error, `postPublicApiData:${path}:${callFn}`);
    throw error;
  }
};
